import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  ActionIcon,
  Stack,
  Select,
  Pagination,
  Modal,
  Textarea,
  SimpleGrid,
  Card,
  Skeleton,
  Avatar,
  ThemeIcon,
  NumberFormatter,
  TextInput,
  Grid,
  Divider,
  Code,
  Alert,
  Popover,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconCheck,
  IconX,
  IconClock,
  IconCircleCheck,
  IconCircleX,
  IconCash,
  IconWallet,
  IconRefresh,
  IconSearch,
  IconDownload,
  IconEye,
  IconBuildingBank,
} from "@tabler/icons-react";
import axios from "axios";
import { ExportModal } from "../../../components/ExportModal";
import "./styles.css";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";

type WithdrawalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "processing"
  | "completed"
  | "failed";

type Withdrawal = {
  id: string;
  user: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    balance?: number;
    level?: number;
  };
  amount: number;
  bankName: string;
  accountNumber: string;
  accountName: string;
  status: WithdrawalStatus;
  processedBy?: {
    _id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  processedAt?: string;
  rejectionReason?: string;
  requestedAt: string;
  createdAt: string;
  transactionReference?: string;
  providerDetails?: any;
};

type WithdrawalStats = {
  pending: number;
  approved: number;
  rejected: number;
  completed: number;
  total: number;
  totalAmount: number;
  totalLiability?: number;
};

interface WithdrawalDetailsModalProps {
  id: string | null;
  onClose: () => void;
}

const WithdrawalDetailsModal: React.FC<WithdrawalDetailsModalProps> = ({
  id,
  onClose,
}) => {
  const endpoint = useMemo(
    () => (id ? Endpoints.GetWithdrawalDetails(id) : null),
    [id],
  );

  const {
    data: withdrawalData,
    isLoading,
    error,
  } = usePerformRequest<{ data: Withdrawal }>({
    method: endpoint?.method || "GET",
    route: endpoint?.route || "",
    skip: !id,
  });

  const withdrawal = withdrawalData?.data;

  const getStatusBadge = (status: WithdrawalStatus) => {
    const config: Record<WithdrawalStatus, { color: string; label: string }> = {
      pending: { color: "yellow", label: "Pending" },
      approved: { color: "brand", label: "Approved" },
      rejected: { color: "red", label: "Rejected" },
      processing: { color: "cyan", label: "Processing" },
      completed: { color: "green", label: "Completed" },
      failed: { color: "red", label: "Failed" },
    };
    const c = config[status];
    return (
      <Badge color={c.color} variant="light" size="sm">
        {c.label}
      </Badge>
    );
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)} Coins`;
  };

  return (
    <Modal
      opened={!!id}
      onClose={onClose}
      title="Withdrawal Details"
      centered
      size="lg"
    >
      {isLoading && <Skeleton height={200} />}
      {error && (
        <Alert color="red" title="Error">
          Failed to load withdrawal details.
        </Alert>
      )}
      {withdrawal && (
        <Stack gap="md">
          <Grid>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                User
              </Text>
              <Text size="sm" fw={500}>
                {[withdrawal.user?.firstName, withdrawal.user?.lastName]
                  .filter(Boolean)
                  .join(" ") || withdrawal.user?.email}
              </Text>
              <Text size="xs" c="dimmed">
                {withdrawal.user?.email}
              </Text>
              <Badge color="yellow" variant="light" size="sm" mt={4}>
                Level {withdrawal.user?.level ?? 1}
              </Badge>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                Amount
              </Text>
              <Text size="sm" fw={500} c="green">
                {formatCurrency(withdrawal.amount)}
              </Text>
            </Grid.Col>
          </Grid>

          <Divider />

          <Grid>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                Bank Name
              </Text>
              <Text size="sm" fw={500}>
                {withdrawal.bankName}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                Account Number
              </Text>
              <Text size="sm" fw={500}>
                {withdrawal.accountNumber}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text size="xs" c="dimmed">
                Account Name
              </Text>
              <Text size="sm" fw={500}>
                {withdrawal.accountName}
              </Text>
            </Grid.Col>
          </Grid>

          <Divider />

          <Grid>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                Status
              </Text>
              {getStatusBadge(withdrawal.status)}
            </Grid.Col>
            <Grid.Col span={6}>
              <Text size="xs" c="dimmed">
                Requested At
              </Text>
              <Text size="sm" fw={500}>
                {formatDate(withdrawal.requestedAt)}
              </Text>
            </Grid.Col>
            {withdrawal.processedAt && (
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Processed At
                </Text>
                <Text size="sm" fw={500}>
                  {formatDate(withdrawal.processedAt)}
                </Text>
              </Grid.Col>
            )}
            {withdrawal.processedBy && (
              <Grid.Col span={6}>
                <Text size="xs" c="dimmed">
                  Processed By
                </Text>
                <Text size="sm" fw={500}>
                  {[
                    withdrawal.processedBy?.firstName,
                    withdrawal.processedBy?.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ") || withdrawal.processedBy?.email}
                </Text>
              </Grid.Col>
            )}
            {withdrawal.rejectionReason && (
              <Grid.Col span={12}>
                <Text size="xs" c="dimmed">
                  Rejection Reason
                </Text>
                <Text size="sm" fw={500} c="red">
                  {withdrawal.rejectionReason}
                </Text>
              </Grid.Col>
            )}
            {withdrawal.transactionReference && (
              <Grid.Col span={12}>
                <Text size="xs" c="dimmed">
                  Transaction Reference
                </Text>
                <Code>{withdrawal.transactionReference}</Code>
              </Grid.Col>
            )}
            {withdrawal.providerDetails && (
              <Grid.Col span={12}>
                <Divider
                  label="Payment Provider Details"
                  labelPosition="center"
                  mb="xs"
                />
                <Paper p="xs" withBorder bg="var(--bg-surface-2)">
                  <Code block style={{ whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(withdrawal.providerDetails, null, 2)}
                  </Code>
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        </Stack>
      )}
    </Modal>
  );
};

const Withdrawals = () => {
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailsId, setDetailsId] = useState<string | null>(null);

  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [approvePopoverId, setApprovePopoverId] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Withdrawals | Virel Admin";
  }, []);

  const withdrawalsEndpoint = useMemo(
    () =>
      Endpoints.GetWithdrawals({
        page: currentPage,
        limit: pageSize,
        status: statusFilter || undefined,
        search: debouncedSearch.trim() || undefined,
        sortBy: "requestedAt",
        sortDir: "desc",
      }),
    [currentPage, pageSize, statusFilter, refreshKey, debouncedSearch],
  );

  const statsEndpoint = useMemo(
    () => Endpoints.GetWithdrawalStats(),
    [refreshKey],
  );

  const { data, isLoading } = usePerformRequest<
    PaginatedResponse<Withdrawal[]>
  >({
    method: withdrawalsEndpoint.method,
    route: withdrawalsEndpoint.route,
    refreshKey: refreshKey,
  });

  const { data: statsData } = usePerformRequest<{ data: WithdrawalStats }>({
    method: statsEndpoint.method,
    route: statsEndpoint.route,
    refreshKey: refreshKey,
  });

  const monnifyBalanceEndpoint = useMemo(
    () => Endpoints.GetMonnifyBalance(),
    [refreshKey],
  );

  const { data: monnifyBalanceResponse, isLoading: isMonnifyBalanceLoading } = usePerformRequest<{
    data: { availableBalance: number; ledgerBalance: number };
  }>({
    method: monnifyBalanceEndpoint.method,
    route: monnifyBalanceEndpoint.route,
    refreshKey: refreshKey,
  });

  const monnifyBalance = monnifyBalanceResponse?.data;

  const withdrawals = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const stats = statsData?.data ?? {
    pending: 0,
    approved: 0,
    rejected: 0,
    completed: 0,
    total: 0,
    totalAmount: 0,
    totalLiability: 0,
  };

  const handleApprove = async (id: string) => {
    setIsProcessing(true);
    const endpoint = Endpoints.ApproveWithdrawal(id);
    await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsProcessing(false);
    setRefreshKey((k) => k + 1);
  };

  const handleReject = async () => {
    if (!selectedId || !rejectionReason.trim()) return;
    setIsProcessing(true);
    const endpoint = Endpoints.RejectWithdrawal(selectedId);
    await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: { reason: rejectionReason },
    });
    setIsProcessing(false);
    setRejectModalOpen(false);
    setSelectedId(null);
    setRejectionReason("");
    setRefreshKey((k) => k + 1);
  };

  const getStatusBadge = (status: WithdrawalStatus) => {
    const config: Record<WithdrawalStatus, { color: string; label: string }> = {
      pending: { color: "yellow", label: "Pending" },
      approved: { color: "brand", label: "Approved" },
      rejected: { color: "red", label: "Rejected" },
      processing: { color: "cyan", label: "Processing" },
      completed: { color: "green", label: "Completed" },
      failed: { color: "red", label: "Failed" },
    };
    const c = config[status];
    return (
      <Badge color={c.color} variant="light" size="sm">
        {c.label}
      </Badge>
    );
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)} Coins`;
  };

  const rows = withdrawals.map((w) => (
    <Table.Tr key={w.id}>
      <Table.Td>
        <Group gap="sm">
          <Avatar size="sm" color="brand" radius="sm">
            {(w.user?.firstName || w.user?.email || "?").charAt(0)}
          </Avatar>
          <div>
            <Text fw={500} size="sm">
              {[w.user?.firstName, w.user?.lastName]
                .filter(Boolean)
                .join(" ") || w.user?.email}
            </Text>
            <Text size="xs" c="dimmed">
              {w.user?.email}
            </Text>
            <Badge size="xs" variant="light" color="yellow" mt={2}>
              Level {w.user?.level ?? 1}
            </Badge>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600} c="green">
          {formatCurrency(w.amount)}
        </Text>
      </Table.Td>
      <Table.Td>
        <div>
          <Text size="sm" fw={500}>
            {w.bankName}
          </Text>
          <Text size="xs" c="dimmed">
            {w.accountNumber}
          </Text>
        </div>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{w.accountName}</Text>
      </Table.Td>
      <Table.Td>{getStatusBadge(w.status)}</Table.Td>
      <Table.Td>
        <Text size="sm">{formatDate(w.requestedAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon
            variant="light"
            color="blue"
            onClick={() => setDetailsId(w.id)}
          >
            <IconEye size={16} />
          </ActionIcon>
          {w.status === "pending" && (
            <>
              <Popover
                opened={approvePopoverId === w.id}
                onClose={() => setApprovePopoverId(null)}
                position="bottom"
                withArrow
                shadow="md"
                width={240}
              >
                <Popover.Target>
                  <ActionIcon
                    variant="light"
                    color="green"
                    onClick={() => setApprovePopoverId(w.id)}
                    loading={isProcessing && approvePopoverId === w.id}
                  >
                    <IconCheck size={16} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Confirm Approval
                    </Text>
                    <Text size="xs" c="dimmed">
                      Approve this withdrawal of{" "}
                      <Text span fw={600} c="green">
                        {formatCurrency(w.amount)}
                      </Text>
                      ?
                    </Text>
                    <Group justify="flex-end" gap="xs">
                      <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => setApprovePopoverId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="compact-xs"
                        color="green"
                        loading={isProcessing}
                        onClick={async () => {
                          await handleApprove(w.id);
                          setApprovePopoverId(null);
                        }}
                      >
                        Confirm
                      </Button>
                    </Group>
                  </Stack>
                </Popover.Dropdown>
              </Popover>
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => {
                  setSelectedId(w.id);
                  setRejectModalOpen(true);
                }}
              >
                <IconX size={16} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const skeletonRows = Array.from({ length: 5 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Group gap="sm">
          <Skeleton height={32} circle />
          <div style={{ flex: 1 }}>
            <Skeleton height={10} width="60%" mb={4} />
            <Skeleton height={8} width="40%" />
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="50%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="60%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={18} width={70} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="50%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={24} width={60} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="cashouts-page">
      <Container size="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1} className="page-title">
                Withdrawals
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                Manage withdrawal requests and payouts
              </Text>
            </div>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Pending
                  </Text>
                  <Text size="xl" fw={700} mt={4} c="yellow">
                    {stats.pending}
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="yellow">
                  <IconClock size={24} />
                </ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Completed
                  </Text>
                  <Text size="xl" fw={700} mt={4} c="green">
                    {stats.completed}
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="green">
                  <IconCircleCheck size={24} />
                </ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Rejected
                  </Text>
                  <Text size="xl" fw={700} mt={4} c="red">
                    {stats.rejected}
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="red">
                  <IconCircleX size={24} />
                </ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Total Paid
                  </Text>
                  <Text size="xl" fw={700} mt={4} c="teal">
                    {formatCurrency(stats.totalAmount)}
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="teal">
                  <IconWallet size={24} />
                </ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Outstanding Liability
                  </Text>
                  <Text size="xl" fw={700} mt={4} c="orange">
                    {formatCurrency(stats.totalLiability || 0)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Users with {">"} 500
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="orange">
                  <IconCash size={24} />
                </ThemeIcon>
              </Group>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    Monnify Balance
                  </Text>
                  {isMonnifyBalanceLoading ? (
                    <Skeleton height={24} width={100} mt={4} />
                  ) : (
                    <Text size="xl" fw={700} mt={4} c="indigo">
                      ₦{(monnifyBalance?.availableBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    Ledger: ₦{(monnifyBalance?.ledgerBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </div>
                <ThemeIcon size="xl" radius="md" variant="light" color="indigo">
                  <IconBuildingBank size={24} />
                </ThemeIcon>
              </Group>
            </Card>
          </SimpleGrid>

          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Group gap="md" mb="md">
              <TextInput
                placeholder="Search withdrawals..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setCurrentPage(1);
                }}
                style={{ flex: 1 }}
              />
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => setExportModalOpen(true)}
              >
                Export
              </Button>
              <Select
                placeholder="Filter by status"
                data={[
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "processing", label: "Processing" },
                  { value: "completed", label: "Completed" },
                  { value: "rejected", label: "Rejected" },
                  { value: "failed", label: "Failed" },
                ]}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val as WithdrawalStatus | null);
                  setCurrentPage(1);
                }}
                clearable
                size="md"
                style={{ width: 180 }}
              />
              <Group gap="xs" align="center" ml="auto">
                <Text size="sm" c="dimmed">
                  Per Page
                </Text>
                <Select
                  data={["5", "10", "20", "50"]}
                  value={String(pageSize)}
                  onChange={(value) => {
                    setPageSize(Number(value) || 10);
                    setCurrentPage(1);
                  }}
                  size="sm"
                  w={80}
                />
              </Group>
            </Group>

            <div className="table-container">
              <Table
                striped
                highlightOnHover
                verticalSpacing="sm"
                withColumnBorders
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Bank</Table.Th>
                    <Table.Th>Account Name</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Requested</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isLoading ? (
                    skeletonRows
                  ) : rows.length > 0 ? (
                    rows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={7} style={{ textAlign: "center" }}>
                        <Text c="dimmed" py="xl">
                          No withdrawal requests found
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            <Group justify="center" mt="md">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => setRefreshKey((k) => k + 1)}
              >
                Reload Withdrawals
              </Button>
            </Group>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>
            )}
          </Paper>
        </Stack>
      </Container>

      <Modal
        opened={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedId(null);
          setRejectionReason("");
        }}
        title="Reject Withdrawal"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Please provide a reason for rejecting this withdrawal request.
          </Text>
          <Textarea
            placeholder="Rejection reason..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setRejectModalOpen(false);
                setSelectedId(null);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={isProcessing}
              disabled={!rejectionReason.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>

      <WithdrawalDetailsModal
        id={detailsId}
        onClose={() => setDetailsId(null)}
      />
      <ExportModal
        opened={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Export Withdrawals"
        type="withdrawals"
        additionalFilters={null}
        onExport={async (dateFilters) => {
          const token = localStorage.getItem("t");
          const endpoint = Endpoints.GetWithdrawalsReport({
            format: "csv",
            ...dateFilters,
          });

          const response = await axios.get(endpoint.route, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `withdrawals_report_${new Date().toISOString()}.csv`,
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      />
    </div>
  );
};

export default Withdrawals;
